import React, { type ReactElement, type ReactNode, useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/app/store";
import { EndUserType } from "../../types/EndUserType"
import { moveEndUser } from "../../hooks/endUser";
import EndUserEditModal from "./EndUserEditModal";
import { EndUserMove } from "../../types/EndUserMove";
import { getDealers } from "src/features/admin/hooks/dealers";
import { deleteEndUserInstance, getEndUserInstancesByEndUserID, insertEndUserInstance } from "src/features/endUser/hooks/endUserInstances";
import EndUserInstanceList from "src/features/endUser/components/Main/EndUserInstanceList";
import { EndUserInstanceType } from "src/features/endUser/types/EndUserInstanceType";
import { createEndUserInstance, updateEndUserInstance, endUserInstanceReset } from "../../../endUser/hooks/endUserInstance";
import { endUserInstanceLocationReset } from "src/features/endUserInstance/hooks/endUserInstanceLocation";
import EndUserInstanceModal from "src/features/endUser/components/Main/EndUserInstanceModal";
import EndUserDetails from "src/features/endUser/components/Main/EndUserDetails";
import EndUserInstanceDetails from "src/features/endUserInstance/components/Main/EndUserInstanceDetails";
import { MACSearchResultType } from "../../types/MACSearchResultType";
import DeleteModal from "src/components/DeleteModal";

export interface EndUserListProps {
        endUserClick: (event: React.MouseEvent<HTMLDivElement>) => void,
        deleteEndUserClick: (event: React.MouseEvent<HTMLDivElement>) => void,
        endUserList: Array<EndUserType>,
        macAddressSearchResult: MACSearchResultType,
        children?: ReactNode
}

const initialEndUserMove = {
        DealerId: 0,
        EndUserId: 0
}

const initialEndUserInstance = {
        Id: 0,
        EndUserInstanceId: "",
        EndUserId: 0,
        Name: "",
        NumberOfDoors: 0,
        Enabled: false,
        Notes: "",
        Link: "",
        CreatedAt: "",
}

const initialEndUser = {
        Id: 0,
        EndUserId: "",
        DealerId: 0,
        Name: "",
        Addresses: {
                Address1: "",
                Address2: "",
                City: "",
                State: "",
                Country: "US",
                PostalCode: "",
                AddressType: ""
        },
        EmailId: "",
        Enabled: false,
        PhoneNumber: "",
        TotalNumberOfDoors: 0,
        Notes: "",
        PrimaryContactEmail: "",
        PrimaryContactPhone: "",
        CreatedAt: "",
        ModifiedAt: "",
}

export default function EndUserList(props: EndUserListProps): ReactElement {
        const [isModalOpen, setModalOpen] = useState(false);
        const [isMoveModalOpen, setMoveModalOpen] = useState(false);
        const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

        const [endUserExpanded, setEndUserExpanded] = useState(props.macAddressSearchResult.EndUserId == 0 ? false : true);
        const [endUserIDSelected, setEndUserIDSelected] = useState(props.macAddressSearchResult.EndUserId);

        const [endUserHeader, setEndUserHeader] = useState("h3");
        const [modalTitle, setModalTitle] = useState("");
        const [deleteModalTitle, setDeleteModalTitle] = useState("");
        const [moveModalTitle, setMoveModalTitle] = useState("");
        const [endUserMove, setEndUserMove] = useState<EndUserMove>(initialEndUserMove);
        const [selectEndUser, setEndUser] = useState<EndUserType>(initialEndUser);
        const { dealer } = useSelector((state: RootState) => state.dealer);
        const { dealersLoading } = useSelector((state: RootState) => state.dealers);
        const { endUserInstancesLoading, endUserInstances } = useSelector((state: RootState) => state.endUserInstances);
        const { endUsersLoading, endUsers } = useSelector((state: RootState) => state.endUsers);
        const { authUser } = useSelector((state: RootState) => state.auth);
        const authorized = authUser?.PasswordProfileId === 1 || authUser?.PasswordProfileId === 2 ? false : true;
        const dispatch = useDispatch<AppDispatch>();
        const [selectedEndUserInstance, setEndUserInstance] = useState<EndUserInstanceType | undefined>(initialEndUserInstance);

        const { endUserInstanceLoading, endUserInstance } = useSelector((state: RootState) => state.endUserInstance);

        const endUserClick = (event: React.MouseEvent<HTMLDivElement>) => {
                props.endUserClick(event);
        }

        const Close = () => {
                setModalOpen(false);
                dispatch(endUserInstanceReset());
        }

        const handleSubmitEndUserInstance = async (data: EndUserInstanceType) => {
                if (modalTitle === "Add EndUserInstance") {
                        await dispatch(createEndUserInstance(data));
                }
                else if (modalTitle === "Edit EndUserInstance") {
                        await dispatch(updateEndUserInstance(data));
                }
        }

        const changeDealer = async (event: React.MouseEvent<HTMLButtonElement>) => {
                setMoveModalOpen(true);
                setMoveModalTitle("Edit EndUserInstance");
                const endUserMove = {
                        DealerId: dealer?.Id,
                        EndUserId: parseInt(event.currentTarget.id)
                }
                setEndUserMove(endUserMove);
                await dispatch(getDealers(""));
        }

        const handleSubmitMoveEndUser = async (data: string) => {
                await dispatch(moveEndUser(data));
        }

        const closeEndUserInstances = () => {
                setEndUserExpanded(false);
                setEndUserIDSelected(0);
                setEndUserHeader("h3");
        }

        const listEndUserInstances = async (endUserID: number) => {
                setEndUserExpanded(true);
                setEndUserHeader("h2");
                setEndUserIDSelected(Number(endUserID));
                let endUser = endUsers.find(a => a.Id === Number(endUserID));
                if (endUser !== undefined) {
                        setEndUser(endUser);
                }
                await dispatch(getEndUserInstancesByEndUserID(endUserID));
        }

        const endUserInstanceClicked = (event: React.MouseEvent<HTMLDivElement>) => {
                const id = event.currentTarget.id;
                setModalOpen(true);
                if (id === "") {
                        setModalTitle("Add EndUserInstance");
                        initialEndUserInstance.EndUserId = Number(endUserIDSelected);
                        setEndUserInstance(initialEndUserInstance);
                }
                else {
                        setModalTitle("Edit EndUserInstance");
                        setEndUserInstance(endUserInstances.find(a => a.Id === Number(id)));
                }
        }

        const deleteEndUserClick = (event: React.MouseEvent<HTMLDivElement>) => {
                props.deleteEndUserClick(event);
        }

        const DeleteEndUserInstanceClicked = (event: React.MouseEvent<HTMLDivElement>) => {
                const id = event.currentTarget.id;
                const endUserInstance = endUserInstances.find(e => e.Id === Number(id));
                setEndUserInstance(endUserInstance);
                setDeleteModalTitle("Delete EndUserInstance");
                setDeleteModalOpen(true);
        }

        const DeleteEndUserInstance = async (doDelete: boolean) => {
                if (doDelete) {
                        await dispatch(deleteEndUserInstance(selectedEndUserInstance));
                }
                setDeleteModalTitle("");
                setDeleteModalOpen(false);
        }

        useEffect(() => {
                if (endUserInstance.Id !== 0) {
                        setModalOpen(false);
                        dispatch(insertEndUserInstance(endUserInstance));
                        dispatch(endUserInstanceReset());
                }
                if (props.macAddressSearchResult.EndUserId != 0) {
                        listEndUserInstances(props.macAddressSearchResult.EndUserId);
                }
        }, [endUserInstance.Id, props])

        return <>
                <div className="grid grid-cols-[1fr_auto] h-full">
                        <div className="flex flex-col gap-1 overflow-auto p-[0.1rem]">
                                {
                                        endUserHeader === "h3" ?
                                                <h3 className="sticky top-0 bg-blue-200 shadow-[0px_0px_5px_rgba(0,0,0,0.25)]">

                                                        <div className="grid grid-cols-[1fr_12fr_2fr_2fr] gap-1">
                                                                <div></div>
                                                                <div className="grid grid-cols-5 p-4 font-semibold text-blue-700">
                                                                        <p className="text-center">Status</p>
                                                                        <p className="text-center">EndUser ID</p>
                                                                        <p className="text-center">Name</p>
                                                                        <p className="text-center">Primary Contact Email</p>
                                                                        <p className="text-center">Primary Contact Phone</p>
                                                                </div>
                                                                <div className="flex items-center justify-center cursor-pointer">
                                                                        <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={endUserClick}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-plus-square"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                                                                        </div>
                                                                </div>
                                                                <div>
                                                                </div>
                                                        </div>
                                                </h3>
                                                :
                                                <h4 className="sticky top-0 bg-blue-100 blur-[1px] shadow-[0px_0px_5px_rgba(0,0,0,0.25)]">
                                                        <div className="grid grid-cols-[1fr_12fr_2fr_2fr] gap-1">
                                                                <div></div>
                                                                <div className="grid grid-cols-5 p-4 font-semibold text-blue-700">
                                                                        <p className="text-center">Status</p>
                                                                        <p className="text-center">EndUser ID</p>
                                                                        <p className="text-center">Name</p>
                                                                        <p className="text-center">Primary Contact Email</p>
                                                                        <p className="text-center">Primary Contact Phone</p>
                                                                </div>
                                                                <div className="flex items-center justify-center cursor-pointer">
                                                                        <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={endUserClick}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-plus-square"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                                                                        </div>
                                                                </div>
                                                                <div>
                                                                </div>
                                                        </div>
                                                </h4>
                                }
                                {
                                        props.endUserList.map((item, index) =>
                                                <div className="grid grid-rows-[1fr_auto]">
                                                        <div className="grid grid-cols-[1fr_12fr_2fr_2fr] gap-1 border-b border-gray-400 p-1">
                                                                {authUser?.EmailId === "admin@dsxinc.com" ?
                                                                        <div onClick={deleteEndUserClick} id={item?.Id.toString()} key={item.Id}>
                                                                                <div className="cursor-pointer w-fit">
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-trash-2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                                                </div>
                                                                        </div>
                                                                        :
                                                                        <div></div>
                                                                }
                                                                {
                                                                        endUserExpanded && endUserIDSelected !== item.Id ?
                                                                                <div className="grid grid-cols-5 blur-[1px] z-1 cursor-pointer hover:bg-gray-300" id={item.Id.toString()} key={item.Id} onClick={endUserClick}>
                                                                                        {
                                                                                                item.Enabled ?
                                                                                                        <div className="flex items-center justify-center">
                                                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-check-circle"><path stroke="green" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                                                                        </div>
                                                                                                        :
                                                                                                        <div className="flex items-center justify-center">
                                                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-x-circle"><circle stroke="red" cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                                                                                        </div>
                                                                                        }
                                                                                        <p className="text-center">{item.EndUserId}</p>
                                                                                        <p className="text-center">{item.Name}</p>
                                                                                        <p className="text-center">{item.PrimaryContactEmail}</p>
                                                                                        <p className="text-center">{item.PrimaryContactPhone}</p>
                                                                                </div>
                                                                                :
                                                                                <div className="grid grid-cols-5 cursor-pointer hover:bg-gray-300" id={item.Id.toString()} key={item.Id} onClick={endUserClick}>
                                                                                        {
                                                                                                item.Enabled ?
                                                                                                        <div className="flex items-center justify-center">
                                                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-check-circle"><path stroke="green" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                                                                        </div>
                                                                                                        :
                                                                                                        <div className="flex items-center justify-center">
                                                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-x-circle"><circle stroke="red" cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                                                                                        </div>
                                                                                        }
                                                                                        <p className="text-center">{item.EndUserId}</p>
                                                                                        <p className="text-center">{item.Name}</p>
                                                                                        <p className="text-center">{item.PrimaryContactEmail}</p>
                                                                                        <p className="text-center">{item.PrimaryContactPhone}</p>
                                                                                </div>
                                                                }
                                                                {endUserExpanded && endUserIDSelected === item.Id ?
                                                                        <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={() => { closeEndUserInstances(); }}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-arrow-up-circle"><circle cx="12" cy="12" r="10"></circle><polyline points="16 12 12 8 8 12"></polyline><line x1="12" y1="16" x2="12" y2="8"></line></svg>
                                                                        </div>
                                                                        :
                                                                        <div className="flex items-center justify-center cursor-pointer shadow-[0px_0px_5px_rgba(0,0,0,0.25)] hover:shadow-[0px_0px_5px_rgba(0,0,0,0.5)]" onClick={() => { listEndUserInstances(item.Id); }}>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-arrow-down-circle"><circle cx="12" cy="12" r="10"></circle><polyline points="8 12 12 16 16 12"></polyline><line x1="12" y1="8" x2="12" y2="16"></line></svg>
                                                                        </div>
                                                                }
                                                                {authUser?.UserType === "admin" ?
                                                                        <div className="flex items-center justify-center">
                                                                                <button id={item.Id.toString()} key={item.Id} className="bg-blue-300 rounded-xs hover:bg-blue-500 text-black font-bold p-2 shadow-[0px_0px_5px_rgba(0,0,0,0.25)]" disabled={authorized} onClick={changeDealer}>Change Dealer</button>
                                                                        </div>
                                                                        : <></>
                                                                }
                                                        </div>
                                                        {endUserExpanded && endUserIDSelected === item.Id ?
                                                                <div>
                                                                        {/* <EndUserInstanceDetails endUserInstance={initialEndUserInstance} /> */}
                                                                        <EndUserInstanceList macAddressSearchResult={props.macAddressSearchResult} endUserInstanceList={endUserInstances} endUserInstanceClick={endUserInstanceClicked} deleteEndUserInstanceClick={DeleteEndUserInstanceClicked} />
                                                                </div>
                                                                :
                                                                <></>
                                                        }
                                                </div>
                                        )
                                }
                                {(isMoveModalOpen && !dealersLoading) ?
                                        <EndUserEditModal title={moveModalTitle} endUserMove={endUserMove} isOpen={isMoveModalOpen} onClose={() => setMoveModalOpen(false)} />
                                        :
                                        <></>
                                }
                        </div>
                        {/* {!endUserToggle ? */}
                        {/*         <></> */}
                        {/*         : */}
                        {/*         <EndUserInstanceList endUserInstanceList={endUserInstances} endUserClick={openEndUserInstanceModal} /> */}
                        {/* } */}

                        {isDeleteModalOpen ?
                                <DeleteModal title={deleteModalTitle} delete={DeleteEndUserInstance} isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} >Do you want to delete Dealer: {selectedEndUserInstance?.Name} ?</DeleteModal>
                                :
                                <></>
                        }
                        {
                                isModalOpen ?
                                        <EndUserInstanceModal endUserID={endUserIDSelected.toString()
                                        } title={modalTitle} selectedEndUserInstance={selectedEndUserInstance} isOpen={isModalOpen} onClose={Close} onSubmit={handleSubmitEndUserInstance} />
                                        :
                                        <></>
                        }
                </div>
        </>
}

